import { useState, useEffect, useRef, useCallback } from 'react';

interface SpinDelayOptions {
  /** Delay in ms before the spinner is displayed. @default 500 */
  delay?: number;
  /** Minimum duration in ms the spinner is displayed. @default 200 */
  minDuration?: number;
  /** Show spinner immediately during SSR if loading is true. @default true */
  ssr?: boolean;
}

type State = 'IDLE' | 'DELAY' | 'DISPLAY' | 'EXPIRE';

const DEFAULT_DELAY = 500;
const DEFAULT_MIN_DURATION = 200;

function useIsSSR() {
  const [isSSR, setIsSSR] = useState(true);
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => setIsSSR(false), []);
  return isSSR;
}

export function useSpinDelay(loading: boolean, options?: SpinDelayOptions): boolean {
  const delay = options?.delay ?? DEFAULT_DELAY;
  const minDuration = options?.minDuration ?? DEFAULT_MIN_DURATION;
  const ssr = options?.ssr ?? true;

  const isSSR = useIsSSR() && ssr;
  const [state, setState] = useState<State>(() => (isSSR && loading ? 'DISPLAY' : 'IDLE'));
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Use a ref to read the latest `loading` value inside timeout callbacks,
  // avoiding stale closures.
  const loadingRef = useRef(loading);
  loadingRef.current = loading;

  const clearPending = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect owns timer lifecycle after render and cleanup.
  useEffect(() => {
    if (loading && (state === 'IDLE' || isSSR)) {
      clearPending();

      const effectiveDelay = isSSR ? 0 : delay;

      timeoutRef.current = setTimeout(() => {
        // Check the *current* loading value, not the closed-over one.
        if (!loadingRef.current) {
          setState('IDLE');
          return;
        }

        setState('DISPLAY');

        timeoutRef.current = setTimeout(() => {
          setState('EXPIRE');
        }, minDuration);
      }, effectiveDelay);

      if (!isSSR) {
        setState('DELAY');
      }
    } else if (!loading && state !== 'DISPLAY') {
      // Loading finished before min duration — go straight to IDLE
      // unless we're in DISPLAY (where we need to wait for minDuration).
      clearPending();
      setState('IDLE');
    }
  }, [loading, state, delay, minDuration, isSSR, clearPending]);

  // Cleanup on unmount
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => clearPending, [clearPending]);

  return state === 'DISPLAY' || state === 'EXPIRE';
}
